import React, { useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  Icon,
  InputAdornment,
  Link,
  makeStyles,
  TextField,
} from "@material-ui/core";
import { Email } from "@material-ui/icons";
import uverifyLogo from "assets/img/uverify-logo.jpg";
import * as z from "zod";

import { tryRedirectToSSO } from "helpers/KeycloakLoginStrategy";
import {
  BackButton,
  Container,
  ContentActions,
  ContentForm,
  ContentLogo,
  LoginButton,
} from "./LoginForm.styles";

import useLogin from "hooks/useLogin";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useCheckExistingUser } from "services/input-core/users/verify-user";
import { useSendResetPassword } from "services/input-core/users/send-mail-reset-password";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      marginBottom: theme.spacing(2),
    },
  },
  inputAdornmentIcon: {
    color: theme.palette.primary.main,
  },
}));

const loginSchema = z.object({
  username: z.string().min(3),
  password: z.string().min(6),
});

type LoginFormFields = z.infer<typeof loginSchema>;

interface LoginFormProps {
  selectedLanguage: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ selectedLanguage }) => {
  const classes = useStyles();

  const { t } = useTranslation("login-page");
  const { signIn } = useLogin();
  const [currentStep, setCurrentStep] = React.useState(0);
  const {
    handleSubmit,
    trigger,
    register,
    watch,
    setValue,
  } = useForm<LoginFormFields>();
  const [loading, setLoading] = React.useState(false);

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const { refetch: checkUserExists } = useCheckExistingUser({
    realm: process.env.REACT_APP_API_AUTH_REALM || "",
    userName: watch("username") || "",
  });

  const {
    mutate: sendResetPassword,
    isLoading: isSendingReset,
  } = useSendResetPassword();

  const trySubmitUserToSSO = async () => {
    setLoading(true);
    const isUsernameValid = await trigger("username");

    if (isUsernameValid) {
      try {
        const { data: userExists } = await checkUserExists();

        if (userExists) {
          !tryRedirectToSSO(watch("username")) ? setCurrentStep(1) : null;

          if (passwordInputRef.current) {
            passwordInputRef.current.focus();
          }
        } else {
          toast.error(t("user-not-found"));
        }
      } catch (error) {
        toast.error(t("error-checking-user"));
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    const username = watch("username");

    if (!username) {
      toast.error(t("please-enter-username-first"));
      return;
    }

    sendResetPassword(
      {
        userName: username,
        realm: process.env.REACT_APP_API_AUTH_REALM || "",
        templateLanguage: selectedLanguage,
      },
      {
        onSuccess: () => {
          toast.success(t("reset-password-email-sent"));
        },
        onError: () => {
          toast.error(t("error-sending-reset-email"));
        },
      }
    );
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter" && currentStep === 0) {
        trySubmitUserToSSO();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [currentStep, trySubmitUserToSSO]);

  const onSubmit = (data: LoginFormFields) => {
    setLoading(true);
    signIn(data.username, data.password).catch(() => {
      setLoading(false);
      if (currentStep === 1) {
        toast.warning(t("login-error"));
      }
    });
  };

  const buttonConfig: any = [
    {
      text: t("next"),
      type: "button",
      onClick: trySubmitUserToSSO,
    },
    {
      text: t("login"),
      type: "submit",
      onClick: undefined,
    },
  ];

  return (
    <Container className={classes.root}>
      <ContentLogo>
        <img src={uverifyLogo} alt="U-verify Logo" />
      </ContentLogo>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ContentForm>
          {currentStep === 0 && (
            <TextField
              label={t("username")}
              id="username"
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Email className={classes.inputAdornmentIcon} />
                  </InputAdornment>
                ),
              }}
              {...register("username", { required: true })}
            />
          )}
          {currentStep === 1 && (
            <>
              <TextField
                label={t("password")}
                id="password"
                fullWidth
                variant="standard"
                type="password"
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon className={classes.inputAdornmentIcon}>
                        lock_outline
                      </Icon>
                    </InputAdornment>
                  ),
                }}
                {...register("password", { required: true })}
                inputRef={passwordInputRef}
              />
              <Box display="flex" justifyContent="end">
                <button
                  disabled={isSendingReset}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    background: "none",
                    border: "none",
                    padding: 0,
                  }}
                  type="button"
                  onClick={handleForgotPassword}
                >
                  {t("forgot-my-password")}
                </button>
              </Box>
            </>
          )}
        </ContentForm>
        <ContentActions>
          <LoginButton
            variant="outlined"
            {...buttonConfig[currentStep]}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              buttonConfig[currentStep].text
            )}
          </LoginButton>
          {currentStep === 1 && (
            <BackButton
              variant="outlined"
              color="primary"
              onClick={() => {
                setCurrentStep(0);
                setValue("password", "");
              }}
              disabled={loading}
            >
              {t("back")}
            </BackButton>
          )}
        </ContentActions>
      </form>
    </Container>
  );
};

export default LoginForm;
